import React, { useState, useRef, useEffect } from "react";
import { cls } from "cls";
import { FlashDealModel, FlashDealItem } from "../Models/PageCreatorPage";
import useCountdown from "~/common/useCountdown";
import FlashDealItems from "./FlashDealItems";

interface FlashDealProps extends FlashDealModel {
  couponCode: string;
  isHeaderGroup?: boolean;
}

const isDealActive = (flashDeal: FlashDealItem | undefined) => {
  const now = Date.now();

  return (
    flashDeal &&
    now >= new Date(flashDeal.startDateTimeUtc).getTime() &&
    now < new Date(flashDeal.endDateTimeUtc).getTime()
  );
};

const FlashDeal = ({
  textColor,
  backgroundColor,
  imageUrl,
  imageAltText,
  items,
  couponCode,
  isHeaderGroup,
}: FlashDealProps) => {
  const [activeFlashDeal, setActiveFlashDeal] = useState<FlashDealItem>();
  const { countdownDisplay, countdownComplete } = useCountdown({
    endDate:
      activeFlashDeal === undefined
        ? undefined
        : new Date(activeFlashDeal.endDateTimeUtc),
  });
  const nextDealActiveTimeout = useRef<number>();
  const activeDealIndex = useRef<number>();

  useEffect(() => {
    if (!countdownComplete && activeFlashDeal) {
      return;
    }

    const nextDealIndex =
      activeDealIndex?.current === undefined ? 0 : activeDealIndex.current + 1;
    const nextDeal = items[nextDealIndex];

    if (isDealActive(nextDeal)) {
      activeDealIndex.current = nextDealIndex;

      setActiveFlashDeal(nextDeal);

      return;
    }

    setActiveFlashDeal(undefined);

    if (nextDeal === undefined) {
      return;
    }

    const millisecondsUntilStart =
      new Date(nextDeal.startDateTimeUtc).getTime() - Date.now();

    if (nextDealActiveTimeout.current) {
      window.clearTimeout(nextDealActiveTimeout.current);
    }

    nextDealActiveTimeout.current = window.setTimeout(() => {
      activeDealIndex.current = nextDealIndex;

      setActiveFlashDeal(nextDeal);

      nextDealActiveTimeout.current = undefined;
    }, millisecondsUntilStart);
  }, [activeFlashDeal, countdownComplete, items]);

  const comingSoonDeals =
    activeFlashDeal === undefined
      ? items.filter(
          (deal) => new Date(deal.endDateTimeUtc).getTime() > Date.now(),
        )
      : items.slice(items.findIndex((deal) => deal === activeFlashDeal) + 1);

  if (activeFlashDeal) {
    return (
      <div
        id="flash-deal"
        className={isHeaderGroup ? cls("container", "py-8") : ""}
      >
        <div
          className={cls(
            "flex",
            "flex-col",
            comingSoonDeals.length > 0 ? "lg:flex-row" : "md:flex-row",
            "gap-4",
            "mx-auto",
            "p-4",
            "rounded",
            comingSoonDeals.length > 0 ? "" : "lg:w-fit",
          )}
          style={{ backgroundColor }}
        >
          <img
            src={imageUrl}
            alt={imageAltText}
            className={`
            ${cls("rounded", "w-auto")}
            sm:max-w-[280px]
            sm:max-h-[280px]
          `}
          />
          <FlashDealItems
            couponCode={couponCode}
            backgroundColor={backgroundColor}
            textColor={textColor}
            headerTextColor={textColor}
            activeFlashDeal={activeFlashDeal}
            comingSoonDeals={comingSoonDeals}
            countdownDisplay={countdownDisplay}
          />
        </div>
      </div>
    );
  }
  return <></>;
};

export default FlashDeal;
